import {
  DEFAULT_FOLDER_COLOR,
  FOLDER_COLORS,
  FOLDER_NAME_MAX_LENGTH,
  FOLDER_ROOT_PATH,
  FolderColorId,
  folderColorValue,
  FolderPath,
  folderPathName,
  isFolderName,
  parentFolderPath,
} from '@lib/library/folderPath';
import { showToast } from '@lib/utils/toastmaster';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { Tooltip } from '@mui/material';
import { LibraryFolderStore } from '@stores/Library/LibraryFolder';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { FolderDialogShell } from './FolderDialogShell';
import css from './FolderDialogs.module.scss';
import { FolderGlyph } from './FolderGlyph';
import barCss from './LibraryFolderBar.module.scss';

export type FolderEditTarget =
  | { kind: 'create'; parent: FolderPath }
  | { kind: 'edit'; path: FolderPath };

/** Why `name` can't be used, or null when it can. Checked as the user types. */
const nameProblem = (name: string, siblings: string[]): string | null => {
  const trimmed = name.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > FOLDER_NAME_MAX_LENGTH) {
    return `Keep it under ${FOLDER_NAME_MAX_LENGTH} characters.`;
  }
  if (!isFolderName(trimmed)) return 'Folder names can’t contain “/” or be “.” or “..”.';
  if (siblings.includes(trimmed)) return 'There’s already a folder with that name here.';
  return null;
};

/** Creates a folder, or renames and recolours one. */
export const FolderEditDialog = observer(function FolderEditDialog({
  folder,
  target,
  onClose,
  onSaved,
}: {
  folder: LibraryFolderStore;
  target: FolderEditTarget;
  onClose: () => void;
  /** With the folder's path once saved — new, or after a rename. */
  onSaved: (path: FolderPath) => void;
}) {
  const existing = target.kind === 'edit' ? folder.foldersByPath.get(target.path) : undefined;
  const parent = target.kind === 'create' ? target.parent : parentFolderPath(target.path);
  const originalName = target.kind === 'edit' ? folderPathName(target.path) : '';

  // A colour this client did not write shows as none, and is only replaced if the user picks one.
  const initialColor = FOLDER_COLORS.find((c) => c.id === existing?.color)?.id ?? null;
  const [name, setName] = useState(originalName);
  const [color, setColor] = useState<FolderColorId | null>(initialColor);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // After the dialog's own focus trap has settled on it.
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, []);

  const siblings = folder
    .childFoldersOf(parent)
    .map((f) => f.name)
    .filter((n) => n !== originalName);
  const problem = nameProblem(name, siblings);
  const unchanged =
    target.kind === 'edit' && name.trim() === originalName && color === initialColor;

  const confirm = async () => {
    try {
      const path =
        target.kind === 'create'
          ? await folder.createFolder(name, parent, color ?? undefined)
          : await folder.updateFolder(target.path, {
              ...(name.trim() !== originalName ? { name } : {}),
              ...(color !== initialColor ? { color } : {}),
            });
      onSaved(path);
      onClose();
    } catch (error) {
      showToast('folder-action-failed', {
        action: target.kind === 'create' ? 'create' : 'edit',
        error,
      });
    }
  };

  return (
    <FolderDialogShell
      modalName="folder-edit"
      open
      onClose={onClose}
      title={target.kind === 'create' ? 'New folder' : 'Edit folder'}
      subtitle={`In ${parent === FOLDER_ROOT_PATH ? (folder.library.name ?? 'this library') : folderPathName(parent)}`}
      confirmLabel={target.kind === 'create' ? 'Create' : 'Save'}
      loadingText={target.kind === 'create' ? 'Creating…' : 'Saving…'}
      confirmDisabled={name.trim().length === 0 || problem !== null || unchanged}
      onConfirm={confirm}
    >
      {/* The folder as it will look in the row under the header — the same pill, in the colour
          picked — so the choice is made against the real thing rather than a dot. */}
      <div
        className={css.preview}
        style={{ '--folder-color': folderColorValue(color ?? undefined) } as React.CSSProperties}
        aria-hidden
      >
        <span className={classNames(barCss.chip, css.previewChip)}>
          <span className={barCss.chipLink}>
            <FolderGlyph color={color ?? undefined} size={24} />
            <span className={classNames(barCss.chipName, !name.trim() && css.previewPlaceholder)}>
              {name.trim() || 'Untitled folder'}
            </span>
          </span>
        </span>
      </div>
      <div className={css.field}>
        <label htmlFor="folder-name">Name</label>
        <input
          ref={inputRef}
          id="folder-name"
          className={classNames(css.input, problem && css.inputInvalid)}
          value={name}
          placeholder="Untitled folder"
          maxLength={FOLDER_NAME_MAX_LENGTH + 1}
          autoComplete="off"
          onChange={(e) => setName(e.target.value)}
        />
        {problem && (
          <span className={classNames(css.hint, css.error)} aria-live="polite">
            {problem}
          </span>
        )}
      </div>
      <div className={css.field}>
        <label id="folder-color-label">Color</label>
        <div className={css.swatches} role="radiogroup" aria-labelledby="folder-color-label">
          {[{ id: null, label: 'Default', value: DEFAULT_FOLDER_COLOR }, ...FOLDER_COLORS].map(
            (swatch) => {
              const checked = color === swatch.id;
              return (
                <Tooltip key={swatch.label} title={swatch.label} placement="top" arrow>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    aria-label={swatch.label}
                    className={classNames(css.swatch, checked && css.selected)}
                    style={{ '--swatch': swatch.value } as React.CSSProperties}
                    onClick={() => setColor(swatch.id)}
                  >
                    {checked && <CheckRounded className={css.swatchCheck} />}
                  </button>
                </Tooltip>
              );
            },
          )}
        </div>
      </div>
    </FolderDialogShell>
  );
});
